import { StarFilled, StarOutlined } from '@ant-design/icons'
import React, { useMemo } from 'react'
import { MalindexConfig, MalindexOmrade } from '../../../api/malindex/malindexApi'
import { Sifferruta, VIKTIGT_ORD } from '../visning'

/**
 * PrioriteradeOmraden – eget steg mellan skattningen och fördjupningen.
 *
 * Att skatta och att välja är olika saker. Skattningen är mekanisk, ett område i
 * taget; prioriteringen är jämförande och kräver att områdena ses mot varandra.
 * Tidigare satt stjärnan i skattningsmatrisen, vilket blandade ihop dem och åt
 * bredd från frågetexten.
 *
 * Områdena visas rangordnade efter hur viktiga brukaren just sagt att de är – det
 * är rankningen spec block 3 efterfrågar. Rangordningen är ett underlag, inte ett
 * facit: det är brukarens prioritering som gäller, och stjärnan går att sätta var
 * som helst i listan.
 *
 * Områden markerade "ej tillämpligt" utesluts – de kan ändå inte prioriteras.
 *
 * Hur viktigt brukaren tyckte att området var bärs av siffrans ruta: en jämn blå ramp
 * från genomskinlig till mörk. Sorteringen ensam räckte inte – två rader efter varandra
 * kan ha samma värde, och då gick det inte att se var "mycket viktigt" slutade.
 *
 * Stegen är appens egen blå-skala (`pejlen` i tailwind.config.js). Färgen sätts här som
 * hex eftersom den ligger i inline-style, inte som klass – ändras skalan där måste de
 * här värdena följa med.
 *
 * Blått, för det är färgen ett valt svar har i skattningsmatrisen. Gult är upptaget av
 * stjärnan och betyder "vald av oss"; blått betyder "sagt av brukaren". Färgen sitter
 * bara i rutan – en toning över hela raden konkurrerade med den gula markeringen och
 * gjorde båda otydliga.
 */

export interface PrioriteradeOmradenProps {
  config: MalindexConfig
  /** Behovssvaren från föregående steg – styr ordningen. */
  svar: Record<number, number | undefined>
  prioriterade: number[]
  onToggle: (omradeId: number) => void
}

const PrioriteradeOmraden: React.FC<PrioriteradeOmradenProps> = ({ config, svar, prioriterade, onToggle }) => {
  const maxPrio = config.maxPrioriterade
  const prioSet = new Set(prioriterade)
  const prioFullt = prioriterade.length >= maxPrio

  const etikettForVarde = useMemo(() => {
    const m = new Map<number, string>()
    config.skala.forEach((s) => m.set(s.varde, s.etikett))
    return m
  }, [config.skala])

  /** Besvarade områden, viktigast först. Ej tillämpligt faller bort helt. */
  const rangordnade: MalindexOmrade[] = useMemo(
    () =>
      config.omraden
        .filter((o) => {
          const v = svar[o.id]
          return v !== undefined && v !== config.ejTillampligt.varde
        })
        .sort((a, b) => (svar[b.id] ?? 0) - (svar[a.id] ?? 0)),
    [config.omraden, config.ejTillampligt.varde, svar]
  )

  return (
    <div className="flex flex-1 flex-col">
      <p className="text-base font-semibold text-gray-800">Vad skulle göra störst skillnad för dig?</p>
      <p className="mb-4 max-w-2xl text-base text-gray-500">
        Områdena står i den ordning brukaren nyss skattade dem. Markera upp till {maxPrio} med stjärnan – det är
        brukarens prioritering som gäller, inte rangordningen.
      </p>

      {rangordnade.length === 0 ? (
        // Sedan skattningen blev tvingande finns bara ett sätt att hamna här: alla tio
        // områdena markerade "ej tillämpligt". "Gå tillbaka och besvara frågorna" vore
        // därför fel råd – de ÄR besvarade.
        <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-base text-gray-500">
          Alla områden är markerade som ej tillämpliga, så det finns inget att prioritera. Gå tillbaka om något ska
          ändras.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rangordnade.map((o) => {
            const prioriterad = prioSet.has(o.id)
            const disabled = !prioriterad && prioFullt
            const varde = svar[o.id] as number

            return (
              <button
                key={o.id}
                type="button"
                aria-pressed={prioriterad}
                disabled={disabled}
                onClick={() => onToggle(o.id)}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  prioriterad
                    ? 'border-yellow-400 bg-yellow-50'
                    : disabled
                    ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                    : 'border-gray-200 bg-white hover:border-yellow-400'
                }`}
              >
                <span className={`text-lg ${prioriterad ? 'text-yellow-500' : 'text-gray-300'}`}>
                  {prioriterad ? <StarFilled /> : <StarOutlined />}
                </span>
                <span className="flex-1 text-base font-medium leading-snug text-gray-800">{o.namn}</span>
                <span className="shrink-0 text-sm text-gray-600">
                  {VIKTIGT_ORD[varde] ?? etikettForVarde.get(varde) ?? varde}
                </span>
                {/* Siffran sist: ordet säger vad, siffran gör raderna jämförbara i ögat. */}
                <Sifferruta varde={varde} />
              </button>
            )
          })}
        </div>
      )}

      <p className="mt-3 text-sm text-gray-500">
        {prioriterade.length} av {maxPrio} prioriterade
      </p>
    </div>
  )
}

export default PrioriteradeOmraden
